import { useEffect, useState, useCallback } from "react";
import { Container, Grid, Typography, CardHeader, Button, MenuItem, TextField } from "@mui/material";
import { useAuth } from "../hooks/useAuth";
import { GetTokenIdentity } from "../utils/Token";
import { Icons } from "../utils/Icon";
import { CustomStyles } from "../utils/Styled";

import axios from "axios";
import MaterialTable from "material-table";

import EditCountrySyncSettingForm from "../components/forms/countrySyncSettings/Edit";

const statusOptions = [
    { id: "", name: "All" },
    { id: 0, name: "Enabled" },
    { id: 1, name: "Disabled" },
];

export const CountrySyncSettingsPage = () => {
    const { token, logout } = useAuth();
    const identity = GetTokenIdentity(token);

    const classes = CustomStyles;

    const [data, setData] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [filter, setFilter] = useState({ status: "" });

    const [open, setOpen] = useState({ edit: false });
    const [selectedRow, setSelectedRow] = useState({});

    const handleOpen = (type, row) => {
        setOpen({ ...open, [type]: true });
        setSelectedRow(row);
    };

    const handleClose = (type) => {
        setOpen({ ...open, [type]: false });
        setSelectedRow(null);
    };

    const handleResetFilter = () => {
        setPage(0);
        setFilter({ status: "" });
    };

    const handleFilterChange = (event) => {
        setPage(0);
        setFilter({ ...filter, [event.target.name]: event.target.value });
    };

    const fetchData = useCallback(async () => {
        try {
            const params = {
                page: page + 1,
                perPage,
            };

            if (filter.status !== "") {
                params.status = filter.status;
            }

            const { data } = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/country-sync-settings", {
                params,
            });

            setData(data.data.countrySyncSettings || []);
            setCount(data.data.total || 0);
        } catch (error) {
            if (error.response && error.response.status === 401) {
                console.log("Unauthorized: ", error);
                logout("Your account has been logged out of a session.");
            } else {
                console.log(error);
            }
        }
    }, [page, perPage, filter, logout]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const columns = [
        { title: "Country", field: "country_name" },
        { title: "Country Code", field: "country_code" },
        { title: "Laravel Base URL", field: "laravel_base_url" },
        {
            title: "Job 4",
            field: "enable_job4_touchpoint_reminder",
            render: (rowData) => (rowData.enable_job4_touchpoint_reminder === 1 ? "Enabled" : "Disabled"),
        },
        {
            title: "Job 5",
            field: "enable_job5_million_product_sync",
            render: (rowData) => (rowData.enable_job5_million_product_sync === 1 ? "Enabled" : "Disabled"),
        },
        {
            title: "Job 6",
            field: "enable_job6_invoice_contract_sync",
            render: (rowData) => (rowData.enable_job6_invoice_contract_sync === 1 ? "Enabled" : "Disabled"),
        },
        {
            title: "Job 7",
            field: "enable_job7_contract_expiry_reminder",
            render: (rowData) => (rowData.enable_job7_contract_expiry_reminder === 1 ? "Enabled" : "Disabled"),
        },
        {
            title: "Job 8",
            field: "enable_job8_customer_sync",
            render: (rowData) => (rowData.enable_job8_customer_sync === 1 ? "Enabled" : "Disabled"),
        },
        {
            title: "Status",
            field: "status",
            render: (rowData) => (rowData.status === 0 ? "Enabled" : "Disabled"),
        },
    ];

    return (
        <div>
            <div className={classes.background} />
            <Container maxWidth="xl" sx={{ py: 2 }} style={{ padding: 14 }}>
                {identity.user.role_id === 1 || identity.user.role_id === 2 ? (
                    <MaterialTable
                        title="Country Sync Setting List"
                        icons={Icons}
                        columns={columns}
                        data={data}
                        page={page}
                        totalCount={count}
                        style={{ padding: "24px" }}
                        options={{
                            pageSize: perPage,
                            pageSizeOptions: [10, 20, 30, 50],
                            search: false,
                            filtering: false,
                            sorting: false,
                            actionsColumnIndex: -1,
                            headerStyle: {
                                fontWeight: "bold",
                                backgroundColor: "#f1f1f1",
                                color: "#212122",
                            },
                            maxBodyHeight: "600px",
                            paginationType: "stepped",
                            padding: "dense",
                            draggable: false,
                        }}
                        localization={{
                            pagination: {
                                labelRowsPerPage: "",
                                labelDisplayedRows: "{from}-{to} of {count}",
                                labelRowsSelect: "rows",
                            },
                            toolbar: {
                                nRowsSelected: "{0} row(s) selected",
                            },
                            header: {
                                actions: "Actions",
                            },
                            body: {
                                emptyDataSourceMessage: "No data to display",
                            },
                        }}
                        actions={[
                            {
                                icon: () => <Icons.Edit />,
                                tooltip: "Edit Country Sync Setting",
                                onClick: (event, rowData) => handleOpen("edit", rowData),
                            },
                        ]}
                        onChangePage={(nextPage) => setPage(nextPage)}
                        onChangeRowsPerPage={(nextPerPage) => setPerPage(nextPerPage)}
                        components={{
                            Toolbar: (props) => (
                                <>
                                    <div>
                                        <Grid container spacing={2} sx={{ px: 0.1, pb: 3.5 }}>
                                            <Grid item xs={12} sm={4}>
                                                <CardHeader
                                                    sx={{ p: 0 }}
                                                    title={
                                                        <Typography component="h1" variant="h5" color="primary" fontWeight="bold">
                                                            Country Sync Setting List
                                                        </Typography>
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </div>

                                    <form>
                                        <Grid container spacing={2} sx={{ paddingBottom: 1.5 }}>
                                            <Grid item xs={12} sm={3} md={2} xl={2}>
                                                <TextField
                                                    fullWidth
                                                    select
                                                    label="Status"
                                                    variant="outlined"
                                                    name="status"
                                                    value={filter.status}
                                                    onChange={handleFilterChange}
                                                    size="small"
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                >
                                                    {statusOptions.map((statusOption) => (
                                                        <MenuItem key={String(statusOption.id)} value={statusOption.id}>
                                                            {statusOption.name}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} sm={1}>
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    color="error"
                                                    sx={{
                                                        color: "#ffffff",
                                                        marginTop: "2px",
                                                    }}
                                                    onClick={handleResetFilter}
                                                >
                                                    Reset
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </form>
                                </>
                            ),
                        }}
                    />
                ) : (
                    <Typography variant="subtitle2">You don't have permission to view country sync settings.</Typography>
                )}

                <EditCountrySyncSettingForm open={open.edit} handleClose={() => handleClose("edit")} rowData={selectedRow} fetchData={fetchData} />
            </Container>
        </div>
    );
};
